import {
  GIVE_BACK_OUTCOME_LABELS,
  GIVE_BACK_REFERENCE_MAX,
  RECONCILE_MARKS,
  REVIEW_VERDICT_LABELS,
  formatInTimeZone,
  expectedMark,
  formatMinor,
  isExceptionMark,
  markClearsException,
  markLabel,
  markNeedsNote,
  minorToInput,
  normalizeGiveBackReference,
  parseMoneyToMinor,
  pinDayConfirmations,
  verdictNeedsNote,
  verdictNeedsRecentAuth,
  verdictsFor,
} from '@didto/domain';
import type {
  GiveBackOutcome,
  NoncashRowView,
  PinDayState,
  ReconcileMark,
  ReviewItemView,
  ReviewVerdict,
} from '@didto/domain';
import type { Tone } from '@didto/ui';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  KvRow,
  Notice,
  Pills,
  SectionHead,
  Sheet,
} from '@didto/ui';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import {
  ACK_STATE_LABEL,
  REVIEW_STATUS_TONE,
  fmtBusinessDate,
  itemTitle,
  readableFacts,
  reviewStatusLabel,
} from '@/lib/review';
import './shifts.css';

/* ── formatting with the business's currency and time zone ── */
export function useFormats() {
  const s = useSession();
  const currency = s.me?.business?.currency ?? 'PHP';
  const tz = s.me?.business?.timezone ?? 'Asia/Manila';
  return useMemo(
    () => ({
      money: (minor: number | null | undefined) =>
        minor == null ? '—' : formatMinor(minor, currency),
      at: (iso: string | null | undefined) => (iso ? formatInTimeZone(iso, tz) : '—'),
      date: (d: string) => fmtBusinessDate(d),
    }),
    [currency, tz],
  );
}

/** A headline figure: a Stat with a line under its value (what it covers, since when). */
export function Figure({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'success' | 'warning' | 'danger';
  testId?: string;
}) {
  return (
    <div className={tone ? `d-stat d-stat--${tone}` : 'd-stat'}>
      <div className="d-stat__label">{label}</div>
      <div className="d-stat__value" data-testid={testId}>
        {value}
      </div>
      {hint ? <div className="rv-stat__hint">{hint}</div> : null}
    </div>
  );
}

/** A read that failed: what happened, in words, and a way to try again. Over what was already loaded, the
 * page keeps showing it (ADR-037) and says it was not refreshed. */
export function ReadError({
  error,
  hasData,
  onRetry,
  testId,
}: {
  error: unknown;
  hasData: boolean;
  onRetry: () => void;
  testId?: string;
}) {
  if (!error) return null;
  if (!hasData) return <ErrorState message={errorText(error)} onRetry={onRetry} testId={testId} />;
  return (
    <Notice
      tone="warning"
      role="status"
      testId={testId}
      title="Not refreshed: showing what was last loaded"
      action={
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      {errorText(error)}
    </Notice>
  );
}

export function ReviewStatusBadge({ status }: { status: string }) {
  return (
    <Badge tone={REVIEW_STATUS_TONE[status] ?? 'neutral'} dot>
      <span data-testid="review-status">{reviewStatusLabel(status)}</span>
    </Badge>
  );
}

/* ── one review item, with the verdicts it takes ── */
export function ReviewItemCard({
  item,
  showDate,
  linkShift = true,
}: {
  item: ReviewItemView;
  /** On the queue, where items of many days are listed. */
  showDate?: boolean;
  /** A way into the item's shift (not on that shift's own page). */
  linkShift?: boolean;
}) {
  const s = useSession();
  const f = useFormats();
  const online = useOnline();
  const { locationId, run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const [sheet, setSheet] = useState<ReviewVerdict | null>(null);
  const [busy, setBusy] = useState(false);
  const ownPin = item.tier === 'pin' && !!item.employee && item.employee.id === s.me?.employee?.id;
  const verdicts = verdictsFor(item, { ownPin });
  const ack = item.ack ?? null;
  const state = ACK_STATE_LABEL[ack?.state ?? 'open'] ?? ACK_STATE_LABEL.open!;
  const facts = readableFacts(item.facts, f);
  const meta = [
    item.employee?.name,
    showDate ? f.date(item.business_date) : null,
    item.since ? f.at(item.since) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const shiftHref = linkShift && item.shift_id ? `/shifts/${item.shift_id}` : null;

  const submit = async (verdict: ReviewVerdict, note: string) => {
    setBusy(true);
    try {
      if (verdictNeedsRecentAuth(verdict) && !(await ensureFresh())) return;
      const r = await run('shift.review_ack', {
        location_id: locationId,
        business_date: item.business_date,
        ...(item.shift_id ? { shift_id: item.shift_id } : {}),
        item_key: item.item_key,
        digest: item.digest,
        verdict,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (r) {
        setSheet(null);
        toast(REVIEW_VERDICT_LABELS[verdict]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="d-row rv-item"
      data-testid="review-item"
      data-kind={item.kind}
      data-state={ack?.state ?? ''}
    >
      <div className="rv-head">
        <div className="d-row__title">{itemTitle(item)}</div>
        {item.amount_minor != null && (
          <span className="rv-money">{f.money(item.amount_minor)}</span>
        )}
      </div>
      {meta && <div className="d-row__meta">{meta}</div>}
      {(item.tier === 'red' || (ack && ack.state !== 'open')) && (
        <div className="rv-item__state">
          {item.tier === 'red' && (
            <Badge tone={state.tone} dot>
              {state.label}
            </Badge>
          )}
          {ack && ack.state !== 'open' && (
            <div className="d-help d-break" data-testid="review-item-ack">
              {ack.verdict ? REVIEW_VERDICT_LABELS[ack.verdict] : ''}
              {ack.note ? ` · “${ack.note}”` : ''}
              {ack.acknowledged_by ? ` · ${ack.acknowledged_by.name}` : ''}
              {ack.acknowledged_at ? ` · ${f.at(ack.acknowledged_at)}` : ''}
            </div>
          )}
        </div>
      )}
      {facts.length > 0 && (
        <details className="rv-details">
          <summary>Details</summary>
          <div className="d-kv">
            {facts.map(([label, value], i) => (
              <KvRow key={`${label}-${i}`} label={label} value={value} />
            ))}
          </div>
        </details>
      )}
      {(shiftHref || verdicts.length > 0) && (
        <div className="rv-item__foot">
          {shiftHref && (
            <Link to={shiftHref} className="d-btn d-btn--sm d-btn--quiet rv-item__link">
              Open the shift<span aria-hidden> ›</span>
            </Link>
          )}
          {verdicts.length > 0 && (
            <div className="rv-item__verdicts">
              {/* the answer that clears the item last, nearest the thumb */}
              {[...verdicts].reverse().map((v) => (
                <Button
                  key={v}
                  variant={v === 'ok' ? 'primary' : v === 'not_mine' ? 'danger' : 'default'}
                  disabled={!online}
                  onClick={() => setSheet(v)}
                  data-testid={`verdict-${v}`}
                >
                  {REVIEW_VERDICT_LABELS[v]}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      {sheet && (
        <VerdictSheet
          verdict={sheet}
          item={item}
          busy={busy}
          onClose={() => setSheet(null)}
          onConfirm={(note) => void submit(sheet, note)}
        />
      )}
    </div>
  );
}

const NOTE_LABEL: Record<ReviewVerdict, string> = {
  ok: 'Why is this fine?',
  follow_up: 'What to follow up',
  resolved: 'How it was resolved',
  not_mine: 'Note',
};

function VerdictSheet({
  verdict,
  item,
  busy,
  onClose,
  onConfirm,
}: {
  verdict: ReviewVerdict;
  item: ReviewItemView;
  busy: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const [note, setNote] = useState('');
  const needsNote = verdictNeedsNote(verdict, item.kind);
  return (
    <Sheet open onClose={onClose} title={REVIEW_VERDICT_LABELS[verdict]}>
      <div className="d-stack" data-testid="verdict-sheet">
        <div>
          <div className="rv-head">
            <div className="d-row__title">{itemTitle(item)}</div>
            {item.amount_minor != null && (
              <span className="rv-money">{f.money(item.amount_minor)}</span>
            )}
          </div>
          {item.employee && <div className="d-row__meta">{item.employee.name}</div>}
        </div>
        {verdict === 'not_mine' && (
          <p className="d-help">
            Your PIN was used for this and it was not you. The item turns red for every owner until
            it is resolved; change your PIN under Employees.
          </p>
        )}
        {(needsNote || verdict !== 'not_mine') && (
          <Field label={NOTE_LABEL[verdict]} help={needsNote ? 'Required' : 'Optional'}>
            {(id) => (
              <Input
                id={id}
                value={note}
                maxLength={300}
                onChange={(e) => setNote(e.target.value)}
                data-testid="verdict-note"
                autoFocus
              />
            )}
          </Field>
        )}
        <Button
          variant={verdict === 'not_mine' ? 'danger' : 'primary'}
          size="lg"
          block
          busy={busy}
          disabled={!online || (needsNote && note.trim().length === 0)}
          onClick={() => onConfirm(note)}
          data-testid="verdict-confirm"
        >
          {REVIEW_VERDICT_LABELS[verdict]}
        </Button>
      </div>
    </Sheet>
  );
}

/* ── non-cash rows and their marks ── */
const ROW_KIND_LABEL: Record<string, string> = {
  payment: '',
  void: 'void',
  refund: 'refund',
  pair: 'paid, then voided',
};

/** Where a row stands, in words: withdrawn or cancelled, its mark, or what is still to do. */
function noncashState(r: NoncashRowView): { label: string; tone: Tone } {
  if (r.withdrawn || r.cancelled)
    return { label: r.withdrawn ? 'Withdrawn' : 'Cancelled', tone: 'neutral' };
  if (r.mark)
    return {
      label: markLabel(r.row_kind, r.mark.mark),
      tone: !isExceptionMark(r.row_kind, r.mark.mark)
        ? 'success'
        : r.mark.mark === 'unverified'
          ? 'warning'
          : 'danger',
    };
  return r.before_start
    ? { label: 'Not due', tone: 'neutral' }
    : { label: 'To check', tone: 'warning' };
}

export function NoncashRows({
  rows,
  batch,
}: {
  rows: NoncashRowView[];
  /** The Day view's "Mark all checked": exactly the unmarked payments on screen. */
  batch?: { business_date: string; ids: string[]; count: number; total_minor: number } | null;
}) {
  const f = useFormats();
  const online = useOnline();
  const [marking, setMarking] = useState<NoncashRowView | null>(null);
  const byMethod = useMemo(() => {
    const m = new Map<string, NoncashRowView[]>();
    for (const r of rows) m.set(r.method, [...(m.get(r.method) ?? []), r]);
    return [...m.entries()];
  }, [rows]);
  if (rows.length === 0) return <EmptyState title="No non-cash payments." />;
  return (
    <div className="d-stack" data-testid="noncash">
      {batch && batch.count > 0 && <BatchCheck batch={batch} />}
      {byMethod.map(([method, list]) => (
        <section key={method} className="d-list" aria-label={method}>
          <div className="d-eyebrow">
            {method} · {list.length}
          </div>
          {list.map((r) => {
            const st = noncashState(r);
            return (
              <div
                key={r.row_id}
                className="d-row"
                data-testid="noncash-row"
                data-mark={r.mark?.mark ?? ''}
                data-row-kind={r.row_kind}
              >
                <div className="d-row__main">
                  <div className="rv-head">
                    <div className="d-row__title">
                      {r.order_number}
                      {r.row_kind !== 'payment' ? ` · ${ROW_KIND_LABEL[r.row_kind]}` : ''}
                      {r.row_kind === 'void' && r.outcome === 'sent_back'
                        ? ' · money sent back'
                        : ''}
                      {r.reference_last4 ? (
                        <span className="d-faint"> · …{r.reference_last4}</span>
                      ) : null}
                    </div>
                    <span className="rv-money">{f.money(r.amount_minor)}</span>
                  </div>
                  <div className="d-row__meta">
                    {f.at(r.recorded_at)}
                    {r.employee ? ` · ${r.employee.name}` : ''}
                    {r.late_entry ? ' · late entry' : ''}
                    {r.possible_duplicate ? ' · possible duplicate reference' : ''}
                    {r.before_start ? ' · before the check started' : ''}
                    {r.corrected && r.recorded_method
                      ? ` · recorded as ${r.recorded_method}, corrected`
                      : ''}
                    {r.reversal_ids.length > 1
                      ? ` · one transfer of ${r.reversal_ids.length} rows`
                      : ''}
                    {r.row_kind === 'void' && r.outcome === null ? ' · not answered' : ''}
                    {r.shift_id === null ? ' · outside a shift, by you' : ''}
                    {r.row_kind === 'pair'
                      ? r.withdrawn
                        ? ' · withdrawn by the owner: nothing to check'
                        : ' · never arrived: expect it in neither direction'
                      : ''}
                    {r.reference_stated
                      ? ` · reference by ${r.reference_stated.by_owner ? 'you' : r.reference_stated.by.name}`
                      : ''}
                  </div>
                  {r.mark && (
                    <div className="d-help" data-testid="noncash-mark">
                      {markLabel(r.row_kind, r.mark.mark)}
                      {r.mark.observed_amount_minor != null
                        ? ` (${f.money(r.mark.observed_amount_minor)})`
                        : ''}
                      {r.mark.note ? ` · “${r.mark.note}”` : ''}
                      {` · ${r.mark.by.name} · ${f.at(r.mark.at)}`}
                      {r.mark.batch_id ? ' · in bulk' : ''}
                    </div>
                  )}
                </div>
                <div className="rv-side">
                  <Badge tone={st.tone} dot>
                    {st.label}
                  </Badge>
                  {!r.withdrawn && !r.cancelled && (
                    <Button
                      size="sm"
                      disabled={!online}
                      onClick={() => setMarking(r)}
                      data-testid="noncash-check"
                    >
                      {r.mark ? 'Check again' : 'Check'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      ))}
      {marking && <MarkSheet row={marking} onClose={() => setMarking(null)} />}
    </div>
  );
}

function MarkSheet({ row, onClose }: { row: NoncashRowView; onClose: () => void }) {
  const f = useFormats();
  const online = useOnline();
  const { run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const previous = row.mark?.mark ?? null;
  const [mark, setMark] = useState<ReconcileMark>(expectedMark(row.row_kind));
  const [observed, setObserved] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const needsNote = markNeedsNote(mark, previous, row.row_kind);
  const clears = markClearsException(row.row_kind, previous, mark);
  const observedMinor = mark === 'mismatch' ? parseMoneyToMinor(observed) : null;
  const valid =
    (mark !== 'mismatch' || (observedMinor != null && observedMinor >= 0)) &&
    (!needsNote || note.trim().length > 0);
  const submit = async () => {
    setBusy(true);
    try {
      if (clears && !(await ensureFresh())) return;
      const r = await run('cash.reconcile', {
        rows: [
          {
            // a pair (a payment that never arrived, with its void) is marked through its payment
            ...(row.row_kind === 'payment' || row.row_kind === 'pair'
              ? { payment_id: row.row_id }
              : { reversal_id: row.row_id }),
            mark,
            ...(mark === 'mismatch' && observedMinor != null
              ? { observed_amount_minor: observedMinor }
              : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          },
        ],
      });
      if (r) {
        toast('Marked');
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title={`${row.order_number} · ${f.money(row.amount_minor)}`}>
      <div className="d-stack" data-testid="mark-sheet">
        <p className="d-muted">
          {row.method}
          {row.reference_last4 ? ` · reference ending ${row.reference_last4}` : ''} ·{' '}
          {f.at(row.recorded_at)}
        </p>
        {row.row_kind === 'pair' && (
          <p className="d-help" data-testid="mark-pair-help">
            The payment never arrived and nothing was sent back: you expect it in the app in neither
            direction.
          </p>
        )}
        <Pills
          ariaLabel="Mark"
          value={mark}
          onChange={setMark}
          options={RECONCILE_MARKS.map((m) => ({ value: m, label: markLabel(row.row_kind, m) }))}
        />
        {mark === 'mismatch' && (
          <Field label="Amount in the provider's record" help="In pesos">
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                className="d-input--money"
                value={observed}
                onChange={(e) => setObserved(e.target.value)}
                data-testid="mark-observed"
              />
            )}
          </Field>
        )}
        <Field
          label={
            mark === 'unverified'
              ? 'Why it could not be checked'
              : clears
                ? 'Why it is fine after all'
                : 'Note'
          }
          help={needsNote ? 'Required' : 'Optional'}
        >
          {(id) => (
            <Input
              id={id}
              value={note}
              maxLength={300}
              onChange={(e) => setNote(e.target.value)}
              data-testid="mark-note"
            />
          )}
        </Field>
        <Button
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online || !valid}
          onClick={() => void submit()}
          data-testid="mark-confirm"
        >
          {markLabel(row.row_kind, mark)}
        </Button>
        {(row.row_kind === 'refund' || row.row_kind === 'void' || row.row_kind === 'pair') && (
          <GiveBackOwnerFields row={row} onDone={onClose} />
        )}
      </div>
    </Sheet>
  );
}

/** The owner's own statements on money given back (ADR-029): the reference of the money sent, and a
 * void's answer. Never inside the refund or the void; the counter's statements stay as history. */
function GiveBackOwnerFields({ row, onDone }: { row: NoncashRowView; onDone: () => void }) {
  const online = useOnline();
  const { run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const voidId =
    row.row_kind === 'pair' ? row.void_id : row.row_kind === 'void' ? row.row_id : null;
  const answered =
    row.outcome === 'sent_back' || row.outcome === 'never_arrived' ? row.outcome : null;
  const [outcome, setOutcome] = useState<GiveBackOutcome | null>(answered);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState<'answer' | 'reference' | null>(null);
  const takesReference = row.row_kind === 'refund' || row.row_kind === 'void';
  const saveOutcome = async () => {
    if (!voidId || !outcome || outcome === answered) return;
    setBusy('answer');
    try {
      // replacing an answer, or answering on a row already marked, needs a fresh authentication
      if ((answered !== null || row.mark !== null) && !(await ensureFresh())) return;
      const r = await run('cash.give_back_outcome', { reversal_id: voidId, outcome });
      if (r) {
        toast('Answer recorded');
        onDone();
      }
    } finally {
      setBusy(null);
    }
  };
  const saveReference = async () => {
    const ref = normalizeGiveBackReference(reference);
    if (!ref) return;
    setBusy('reference');
    try {
      const r = await run('cash.give_back_reference', { reversal_id: row.row_id, reference: ref });
      if (r) {
        toast('Reference recorded');
        onDone();
      }
    } finally {
      setBusy(null);
    }
  };
  if (row.withdrawn || row.cancelled) return null;
  return (
    <>
      <hr className="d-divider" />
      <div className="d-stack" data-testid="give-back-owner">
        <div className="d-eyebrow">Money given back</div>
        {voidId && (
          <>
            <Pills
              ariaLabel="What the void was"
              value={outcome ?? ''}
              onChange={(v) => setOutcome(v as GiveBackOutcome)}
              options={(['sent_back', 'never_arrived'] as const).map((o) => ({
                value: o,
                label: GIVE_BACK_OUTCOME_LABELS[o],
              }))}
            />
            {row.outcome_stated && (
              <p className="d-help">
                Answered by {row.outcome_stated.by_owner ? 'you' : row.outcome_stated.by.name}
              </p>
            )}
            <Button
              block
              busy={busy === 'answer'}
              disabled={!online || !outcome || outcome === answered || busy !== null}
              onClick={() => void saveOutcome()}
              data-testid="give-back-owner-answer"
            >
              Save the answer
            </Button>
          </>
        )}
        {takesReference && outcome !== 'never_arrived' && (
          <Field label="Reference of the money sent">
            {(id) => (
              <div className="rv-inline-form">
                <Input
                  id={id}
                  value={reference}
                  maxLength={GIVE_BACK_REFERENCE_MAX}
                  placeholder={
                    row.reference_last4
                      ? `Reference ending ${row.reference_last4}`
                      : 'Reference of the money sent'
                  }
                  onChange={(e) => setReference(e.target.value)}
                  data-testid="give-back-owner-reference"
                />
                <Button
                  busy={busy === 'reference'}
                  disabled={!online || !normalizeGiveBackReference(reference) || busy !== null}
                  onClick={() => void saveReference()}
                >
                  Save
                </Button>
              </div>
            )}
          </Field>
        )}
      </div>
    </>
  );
}

function BatchCheck({
  batch,
}: {
  batch: { business_date: string; ids: string[]; count: number; total_minor: number };
}) {
  const f = useFormats();
  const online = useOnline();
  const { locationId, run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const [stated, setStated] = useState('');
  const [busy, setBusy] = useState(false);
  const statedMinor = parseMoneyToMinor(stated);
  const submit = async () => {
    if (statedMinor == null) return;
    if (!(await ensureFresh())) return;
    setBusy(true);
    const r = await run('cash.reconcile_all', {
      location_id: locationId,
      business_date: batch.business_date,
      row_ids: batch.ids,
      stated_total_minor: statedMinor,
    });
    setBusy(false);
    if (r) {
      toast(`${String(r.row_count)} rows marked as found`);
      setStated('');
    }
  };
  // the total is the first amount in this block: the merchant app's total is checked against it
  return (
    <Notice
      tone="info"
      testId="batch-check"
      title={
        <>
          {batch.count} {batch.count === 1 ? 'payment' : 'payments'} not yet checked ·{' '}
          <span className="d-mono">{f.money(batch.total_minor)}</span>
        </>
      }
    >
      <div className="d-stack d-stack--tight">
        <p className="d-help">
          Type the merchant app&apos;s total for these payments. They are marked as found only when
          it matches exactly; otherwise check them one by one.
        </p>
        <div className="rv-inline-form">
          <Input
            inputMode="decimal"
            className="d-input--money"
            placeholder="Total in the merchant app"
            value={stated}
            onChange={(e) => setStated(e.target.value)}
            aria-label="Total in the merchant app"
            data-testid="batch-total"
          />
          <Button
            variant="primary"
            busy={busy}
            disabled={!online || statedMinor == null}
            onClick={() => void submit()}
            data-testid="batch-confirm"
          >
            Mark all checked
          </Button>
        </div>
      </div>
    </Notice>
  );
}

/* ── done with your PIN ── */
export function PinDaySection({ state }: { state: PinDayState }) {
  const s = useSession();
  const f = useFormats();
  const online = useOnline();
  const { locationId, run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const mine = state.owner.id === s.me?.employee?.id;
  const [received, setReceived] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const toConfirm = state.items.filter(
    (i) => i.ack.state !== 'not_mine' && i.ack.state !== 'closed',
  );
  const receivedMinor = Object.fromEntries(
    Object.entries(received).map(([k, v]) => [k, parseMoneyToMinor(v) ?? undefined]),
  );
  const confirmAll = async () => {
    if (!(await ensureFresh())) return;
    setBusy(true);
    const r = await run('shift.confirm_pin_day', {
      location_id: locationId,
      business_date: state.business_date,
      items: pinDayConfirmations(toConfirm, receivedMinor),
    });
    setBusy(false);
    if (r) toast('Confirmed as yours');
  };
  return (
    <section
      className="rv-section"
      aria-label={`Done with the PIN of ${state.owner.name}`}
      data-testid="pin-day"
    >
      <div>
        <SectionHead title={`Done with ${mine ? 'your' : `${state.owner.name}'s`} PIN`} />
        <p className="d-help">
          {state.confirmed
            ? `Confirmed ${f.at(state.confirmed_at)}`
            : `${state.unconfirmed} of ${state.total} not confirmed`}
        </p>
      </div>
      <div className="d-list">
        {state.items.map((i) => (
          <div key={i.item_key} className="d-stack d-stack--tight">
            <ReviewItemCard item={i} />
            {i.confirmed ? (
              <div className="d-help" data-testid="pin-confirmed">
                Confirmed
                {i.received_minor != null ? ` · received ${f.money(i.received_minor)}` : ''}
              </div>
            ) : mine &&
              i.kind === 'pin.removal' &&
              i.ack.state !== 'not_mine' &&
              i.ack.state !== 'closed' ? (
              <Field label="Cash you received" help="In pesos; the amount handed over at the close">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    className="d-input--money"
                    value={received[i.item_key] ?? minorToInput(i.amount_minor ?? 0)}
                    onChange={(e) => setReceived({ ...received, [i.item_key]: e.target.value })}
                    data-testid="pin-received"
                  />
                )}
              </Field>
            ) : null}
          </div>
        ))}
      </div>
      {mine && !state.confirmed && toConfirm.length > 0 && (
        <div className="rv-foot">
          <Button
            variant="primary"
            busy={busy}
            disabled={!online}
            onClick={() => void confirmAll()}
            data-testid="pin-all-mine"
          >
            All mine
          </Button>
        </div>
      )}
    </section>
  );
}
